import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug, fetchTeaching, fetchEvents, fetchMusic, fetchGalleries, fetchNoticeboard } from '../lib/cms';
import ReviewSlider from '../components/ReviewSlider';
import GalleryModal from '../components/GalleryModal';
import ScrollReveal from '../components/ScrollReveal';

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [teaching, setTeaching] = useState([]);
  const [events, setEvents] = useState([]);
  const [music, setMusic] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [noticeboard, setNoticeboard] = useState([]);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  // Gallery state
  const [selectedGalleryImages, setSelectedGalleryImages] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  useEffect(() => {
    async function loadHomeData() {
      const [page, teachingData, eventsData, musicData, galleryData, noticeboardData] = await Promise.all([
        fetchPageBySlug('home'),
        fetchTeaching(),
        fetchEvents(),
        fetchMusic(),
        fetchGalleries(),
        fetchNoticeboard(),
      ]);

      setPageData(page);
      setTeaching(teachingData);
      setEvents(eventsData);
      setMusic(musicData);
      setGalleries(galleryData);
      setNoticeboard(noticeboardData);
      setLoading(false);
    }
    loadHomeData();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center bg-sand-50">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-clay-500 border-t-transparent" />
      </div>
    );
  }

  // Fallback hero image if CMS doesn't have one
  const heroImage = pageData?.hero_image?.[0] || 'https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/billede.jpg';

  // Get a few selected images for the home page gallery
  const homeGalleryImages = galleries.flatMap(g => g.images || []);
  const previewImages = homeGalleryImages.slice(0, 6);

  const openLightbox = (index) => {
    setSelectedGalleryImages(homeGalleryImages);
    setActiveImageIndex(index);
  };

  return (
    <div className="bg-sand-50 text-foreground">
      {/* 1. HERO SECTION */}
      <section className="relative min-h-[75vh] md:min-h-[80vh] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={`${heroImage}?width=1920&quality=85&format=auto`}
            alt="Heidi Svelmøe"
            className="h-full w-full object-cover object-center filter brightness-95"
          />
          {/* Top-down dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-transparent" />
          {/* Ultra-smooth bottom-up fade into the sand-50 background */}
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-sand-50 via-sand-50/70 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 mx-auto max-w-4xl px-6 py-16 md:py-24 text-center text-white">
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-wide leading-tight drop-shadow-sm mb-5">
            Sangundervisning & Mindfulness: <br className="hidden sm:inline" />
            Tag din stemme til næste niveau
          </h1>
          <p className="mx-auto max-w-2xl text-sm sm:text-base md:text-lg text-sand-100 leading-relaxed drop-shadow-sm mb-8">
            Find ro i krop og sind. Træn din modighedsmuskel og udtryk dig frit gennem din personlige stemme.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/undervisning"
              className="rounded-full bg-cta px-8 py-3.5 text-sm uppercase tracking-widest font-semibold text-clay-900 hover:bg-cta-hover transition-all shadow-lg hover:shadow-xl active:scale-98"
            >
              SE UNDERVISNING
            </Link>
            <Link
              to="/blog"
              className="rounded-full bg-white/20 backdrop-blur-md border border-white/50 px-8 py-3.5 text-sm uppercase tracking-widest font-semibold text-white hover:bg-white hover:text-clay-900 transition-all shadow-lg hover:shadow-xl active:scale-98"
            >
              LÆS BLOGGEN
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-white/60 text-xs uppercase tracking-widest animate-bounce">
          <span>Scroll</span>
          <i className="fa-light fa-chevron-down text-sm" />
        </div>
      </section>

      {/* 2. MØD DIN UNDERVISER */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <ScrollReveal className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Image */}
            <div className="lg:col-span-5 relative order-2 lg:order-1 flex flex-col gap-8">
              <img
                src="https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/heidi-svelmoe-portraet.webp?width=600&quality=85&format=auto"
                alt="Heidi Svelmøe"
                className="relative z-10 w-full rounded-none shadow-xl object-cover aspect-[3/4]"
              />
              <img
                src="https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/img-4488.jpeg?width=600&quality=85&format=auto"
                alt="Solsalen med åben dør ud til Eventyrhaven"
                className="relative z-10 w-full rounded-none shadow-xl object-cover aspect-[3/4]"
              />
            </div>

            {/* Text Content */}
            <div className="lg:col-span-7 flex flex-col gap-6 order-1 lg:order-2 text-left">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide text-black leading-tight">
                Mød din underviser
              </h2>
              <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
                Hej og velkommen til. Jeg er Heidi. Sang, musik og mindfulness er min store passion, og som sanger og sangskriver har det altid været en stor del af mit liv.
              </p>
              <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
                I min undervisning brænder jeg for at dele denne glæde med dig. Mit fokus er at skabe en tryg forbindelse mellem din krop og dit sind ved at bruge vejrtrækning og støtte samt træne grundlæggende, dybdegående sangteknik. Jeg hjælper dig med at frigøre din stemme, afhjælpe spændinger, opbygge mod og finde dit helt eget, personlige udtryk.
              </p>

              <div>
                <button
                  type="button"
                  onClick={() => setIsBioExpanded(!isBioExpanded)}
                  className="inline-flex items-center gap-2 rounded-full bg-sand-100 hover:bg-sand-200 text-black px-6 py-2.5 text-xs uppercase tracking-widest font-bold transition-all"
                >
                  {isBioExpanded ? 'Læs mindre' : 'Læs mere…'}
                  <i className={`fa-solid ${isBioExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-[10px]`} />
                </button>
              </div>

              {/* Expandable Extended Text */}
              {isBioExpanded && (
                <div className="mt-4 pt-6 border-t border-sand-200/80 flex flex-col gap-5 text-foreground/80 leading-relaxed animate-fadeIn">
                  <h3 className="font-serif text-2xl sm:text-3xl font-bold text-black leading-tight">
                    Sangundervisning & Mindfulness i Odense C: Find din stemme, din ro og dit sande udtryk
                  </h3>
                  <p className="font-semibold text-clay-900">
                    Til dig, der elsker at synge, og som vil kombinere professionel sangteknik med mental lethed og frigøre dit fulde potentiale.
                  </p>
                  <p>
                    Kan du synge, og elsker du det kreative rum? Måske spænder præstationspres, tankemylder eller ubevidste spændinger ben for din stemme?
                  </p>
                  <p>
                    Jeg tilbyder et trygt og professionelt rum i hjertet af Odense. Med min solide baggrund som sanger, sangskriver og sangunderviser guider jeg dig igennem både den tekniske og den følelsesmæssige del af sangen. Her kombinerer vi dybdegående sangteknik og kunstnerisk udtryk med mindfulness, så du kan mærke og lande i din krop, slippe kontrollen og synge med større frihed og kapacitet.
                  </p>
                  <h4 className="font-serif text-xl font-bold text-black mt-2">
                    Hvorfor kombinere sangteknik og mindfulness?
                  </h4>
                  <p>
                    Når du synger, kommer du naturligt i kontakt med din krop og de følelser, du gemmer på. Selvom du er vant til at synge, kan det føles grænseoverskridende at give helt slip. Men når du bevidst bruger mindfulness til at flytte fokus fra hovedet og ned i kroppen, kan præstationsangsten i stedet blive til ekstra energi, der kan bruges konstruktivt. Det er her, dit sande, rå og autentiske udtryk bliver forløst.
                  </p>
                  <p>
                    Undervisningen foregår i den skønne Solsal lige ved Eventyrhaven. Her træder du ind i et lyst og roligt rum, hvor der er højt til loftet, naturskøn udsigt og plads til at trække vejret helt ned i maven.
                  </p>
                  <p className="font-semibold text-black">
                    Vi fordyber os i arbejdet med at:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li><strong className="text-black">Mestre sangteknikken:</strong> Få konkrete værktøjer til en sund, stærk og fleksibel stemme.</li>
                    <li><strong className="text-black">Synge fra kroppen:</strong> Lær at mestre din vejrtrækning/støtte og bruge din krop som instrument.</li>
                    <li><strong className="text-black">Styrke det kunstneriske udtryk:</strong> Formidl fra hjertet og med følelsernes mangfoldighed, så både du selv og dit publikum mærker dig.</li>
                    <li><strong className="text-black">Frigøre blokeringer:</strong> Bearbejd fysiske spændinger og mentale barrierer på en tryg og sjov måde.</li>
                    <li><strong className="text-black">Åbne op for nye lag:</strong> Gå på opdagelse i din stemmes fulde potentiale og dit kreative råderum.</li>
                    <li><strong className="text-black">Finde stor ro:</strong> Oplev sangen som en kilde til fornyet energi, mental lethed og hverdagsglæde.</li>
                  </ul>
                  <div className="mt-4">
                    <Link
                      to="/kontakt"
                      className="inline-flex items-center justify-center rounded-full bg-cta px-8 py-3.5 text-sm uppercase tracking-widest font-semibold text-clay-900 hover:bg-cta-hover transition-all shadow-md active:scale-98"
                    >
                      Tag din stemme til næste niveau – book din session i Odense C her
                    </Link>
                  </div>
                </div>
              )}

              <div className="mt-2">
                <Link
                  to="/om-heidi"
                  className="inline-flex items-center gap-2 text-sm uppercase tracking-widest font-bold text-black hover:text-black/70 transition-colors group"
                >
                  Læs hele historien
                  <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CITAT Sektion 1 */}
      <section className="bg-gray-100 text-gray-800 py-16 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 0)', backgroundSize: '24px 24px' }} />
        </div>
        <div className="relative z-10 mx-auto max-w-3xl px-6">
          <i className="fa-solid fa-quote-left text-3xl text-gray-300 mb-4" />
          <blockquote className="font-serif text-xl sm:text-2xl md:text-3xl italic leading-relaxed tracking-wide">
            "Musik kan påvirke os enormt stærkt og sang er en kraftfuld og følsom udtryksform. Man bliver simpelthen lettet, forløst og glad af at synge."
          </blockquote>
        </div>
      </section>

      {/* 3. UNDERVISNING */}
      <section className="py-20 md:py-28 bg-sand-50">
        <div className="mx-auto max-w-7xl px-6">
          <ScrollReveal className="text-left max-w-3xl mr-auto mb-16 flex flex-col gap-4">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide text-black">
              Sangundervisning & Mindfulness
            </h2>
            <p className="text-base text-foreground/70 leading-relaxed">
              Min undervisning tager altid udgangspunkt i dig, din krop, din stemme og dine ønsker. Vi arbejder med at skabe forbindelse mellem sind og krop, så du kan synge sundt og afslappet.
            </p>
          </ScrollReveal>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teaching.slice(0, 3).map((item, index) => (
              <ScrollReveal key={item.id} delay={index * 100}>
                <Link
                  to="/undervisning"
                  className="group flex flex-col bg-white rounded-none shadow-sm hover:shadow-md border border-sand-200/60 overflow-hidden transition-all hover:-translate-y-1 h-full"
                >
                  <div className="h-48 overflow-hidden relative">
                    <img
                      src={`${item.image?.[0]}?width=400&height=300&fit=cover&format=auto`}
                      alt={item.title.da}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-grow gap-3">
                    {item.price?.da ? (
                      <span className="self-start inline-flex items-center gap-1.5 text-gray-600 text-xs font-bold">
                        {item.price.da}
                      </span>
                    ) : (
                      <span className="self-start inline-flex items-center gap-1.5 text-xs font-bold invisible select-none" aria-hidden="true">
                        &nbsp;
                      </span>
                    )}
                    <h3 className="font-serif text-xl font-bold text-black group-hover:text-black transition-colors">
                      {item.title.da}
                    </h3>
                    <p className="text-sm text-foreground/70 leading-relaxed flex-grow line-clamp-3">
                      {item.short_description?.da}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-black hover:text-black/70 transition-colors mt-2">
                      Læs mere <i className="fa-solid fa-chevron-right text-[10px]" />
                    </span>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CITAT Sektion 2 */}
      <section className="bg-cta text-clay-900 py-16 text-center relative overflow-hidden">
        <div className="relative z-10 mx-auto max-w-3xl px-6">
          <i className="fa-solid fa-quote-left text-3xl text-clay-800/20 mb-4" />
          <blockquote className="font-serif text-xl sm:text-2xl md:text-3xl italic leading-relaxed tracking-wide text-clay-900">
            "At synge er en super god metode til, at flytte fokus lidt væk fra hovedet og tankerne og i stedet komme ned og mærke kroppen."
          </blockquote>
        </div>
      </section>

      {/* 4. MUSIK */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-6 pb-16 border-b border-sand-200/80">
          <ScrollReveal className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left: Text Content */}
            <div className="lg:col-span-7 flex flex-col gap-6 text-left">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide text-black leading-tight">
                Oplev det musikalske univers
              </h2>
              <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
                Min musik strækker sig over several udgivelser, fra mit debutalbum <em>Audioballerina</em> (2008), skabt i samarbejde med Thomas Blachman og Kasper Winding, til <em>Talisman</em> (2011) og mit seneste album <em>Breaking Through The Firewall</em> (2021) med bandet StellarPirate.
              </p>
              <p className="text-sm md:text-base text-foreground/70 leading-relaxed">
                Sangene på mit seneste album er skrevet over en 10-årig periode fyldt med store personlige udfordringer og forandringer. Artworket symboliserer transformationen, at sætte sig selv fri og genopfinde sig selv.
              </p>

              {/* Album Covers Row */}
              <div className="flex gap-4 mt-4 overflow-x-auto pb-2">
                {[...music]
                  .sort((a, b) => {
                    const order = {
                      'breaking-through-the-firewall': 1,
                      'talisman': 2,
                      'audioballerina': 3,
                    };
                    const orderA = order[a.slug?.da] || 99;
                    const orderB = order[b.slug?.da] || 99;
                    return orderA - orderB;
                  })
                  .map((album) => (
                  <div key={album.id} className="flex flex-col gap-2 w-24 sm:w-28 shrink-0">
                    <img
                      src={`${album.cover_image?.[0]}?width=150&height=150&fit=cover&format=auto`}
                      alt={album.title.da}
                      className="w-full aspect-square rounded-none shadow-md border border-sand-200"
                    />
                    <span className="text-[10px] text-center font-medium text-foreground/80 truncate">
                      {album.title.da}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-4">
                <Link
                  to="/musik"
                  className="rounded-full bg-cta px-8 py-3.5 text-xs uppercase tracking-widest font-semibold text-clay-900 hover:bg-cta-hover transition-all shadow-md active:scale-98"
                >
                  Udforsk musikken
                </Link>
              </div>
            </div>

            {/* Right: Album Highlight Cover */}
            <div className="lg:col-span-5 flex justify-center items-center">
              <div className="max-w-sm w-full">
                <img
                  src="https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/breaking-through-the-firewall.webp?width=400&height=400&fit=cover&format=auto"
                  alt="Breaking Through The Firewall Album Cover"
                  className="w-full aspect-square rounded-none object-cover shadow-lg"
                />
                <div className="text-center mt-4">
                  <h3 className="font-serif text-lg font-bold text-black">
                    Breaking Through The Firewall
                  </h3>
                  <p className="text-xs text-sage-600 font-medium uppercase tracking-wider mt-1">
                    Seneste Album (2021)
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* 5. ARRANGEMENTER */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div className="text-left max-w-2xl">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide text-black">
                Kurser, workshops & koncerter
              </h2>
            </div>
            <Link
              to="/kontakt"
              className="rounded-full bg-cta px-6 py-2.5 text-xs uppercase tracking-widest font-semibold text-clay-900 hover:bg-cta-hover transition-all hover:shadow-md active:scale-98 shrink-0"
            >
              Book en tid
            </Link>
          </div>

          {/* Events List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {events.map((event) => {
              const eventDate = new Date(event.date);
              const formattedDate = eventDate.toLocaleDateString('da-DK', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              });

              return (
                <div
                  key={event.id}
                  className="flex flex-col sm:flex-row bg-white rounded-none shadow-sm border border-sand-200/60 overflow-hidden"
                >
                  <div className="sm:w-1/3 h-48 sm:h-auto relative">
                    <img
                      src={`${event.image?.[0]}?width=300&height=400&fit=cover&format=auto`}
                      alt={event.title.da}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-6 sm:w-2/3 flex flex-col justify-between gap-4 text-left">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 text-xs text-sage-600 font-medium uppercase tracking-wider">
                        <i className="fa-light fa-calendar" />
                        <span>{formattedDate}</span>
                        {event.time?.da && (
                          <>
                            <span className="text-sand-300">•</span>
                            <span>{event.time.da}</span>
                          </>
                        )}
                      </div>
                      <h3 className="font-serif text-xl font-bold text-black">
                        {event.title.da}
                      </h3>
                      {event.location?.da && (
                        <p className="text-xs text-foreground/60 flex items-center gap-1.5">
                          <i className="fa-light fa-location-dot" />
                          {event.location.da}
                        </p>
                      )}
                      <div
                        className="text-sm text-foreground/75 leading-relaxed mt-2 prose prose-sand max-w-none prose-a:text-clay-700 prose-a:underline hover:prose-a:text-black"
                        dangerouslySetInnerHTML={{ __html: event.description?.da }}
                      />
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-sand-100 pt-4 mt-2">
                      <span className="text-sm font-bold text-clay-700">
                        Pris: {event.price?.da || 'Gratis'}
                      </span>
                      <div className="flex flex-wrap items-center gap-3">
                        {event.link && (
                          <a
                            href={event.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1877F2] hover:underline"
                            title="Se opslaget hos Solvognen på Facebook"
                          >
                            <i className="fa-brands fa-facebook text-sm" aria-hidden="true" />
                            <span className="hidden sm:inline">Facebook</span>
                          </a>
                        )}
                        {event.booking_link ? (
                          <a
                            href={event.booking_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-full bg-cta hover:bg-cta-hover text-clay-900 px-4 py-2 text-xs uppercase tracking-widest font-semibold transition-all active:scale-98 shadow-xs"
                          >
                            <span>Bestil billet</span>
                            <i className="fa-solid fa-arrow-up-right-from-square text-[9px]" aria-hidden="true" />
                          </a>
                        ) : (
                          <Link
                            to="/kontakt"
                            className="text-xs uppercase tracking-widest font-bold text-black hover:text-black/70 transition-colors"
                          >
                            Tilmeld her <i className="fa-solid fa-chevron-right text-[9px]" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OPSLAGSTAVLE */}
      {noticeboard && noticeboard.length > 0 && (
        <section className="py-20 md:py-28 bg-white border-t border-sand-200/80">
          <div className="mx-auto max-w-7xl px-6">
            <div className="text-center max-w-3xl mx-auto mb-14 flex flex-col gap-4">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide text-black">
                Opslagstavle
              </h2>
              <p className="text-base text-foreground/70 leading-relaxed">
                Aktuelle glimt, opdateringer og videoer fra hverdagen i Solsalen og musikkens verden.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {noticeboard.map((item) => {
                const videoSrc = item.video_file?.[0] || item.video_url;
                const imageSrc = item.image?.[0];

                return (
                  <div
                    key={item.id}
                    className="flex flex-col bg-sand-50/70 rounded-none border border-sand-200 shadow-sm overflow-hidden text-left"
                  >
                    {videoSrc ? (
                      <div className="w-full aspect-video bg-black relative">
                        <video
                          src={videoSrc}
                          controls
                          playsInline
                          preload="none"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : imageSrc ? (
                      <div className="w-full aspect-[16/10] overflow-hidden bg-sand-100">
                        <img
                          src={`${imageSrc}?width=600&quality=85&format=auto`}
                          alt={item.title?.da || 'Opslag'}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : null}

                    <div className="p-6 flex flex-col flex-grow gap-3">
                      {item.date && (
                        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-sage-600 font-semibold">
                          <i className="fa-light fa-calendar" />
                          <span>
                            {new Date(item.date).toLocaleDateString('da-DK', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })}
                          </span>
                        </div>
                      )}
                      <h3 className="font-serif text-xl font-bold text-black">
                        {item.title?.da}
                      </h3>
                      {item.content?.da && (
                        <div
                          className="prose prose-sand prose-sm text-foreground/80 leading-relaxed flex-grow"
                          dangerouslySetInnerHTML={{ __html: item.content.da }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 rounded-full border border-clay-800 px-8 py-3 text-xs uppercase tracking-widest font-semibold text-clay-900 hover:bg-clay-900 hover:text-white transition-all shadow-xs"
              >
                <span>GÅ TIL BLOGGEN FOR FLERE INDLÆG</span>
                <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* 6. GALLERI PREVIEW */}
      <section className="py-20 md:py-28 bg-sand-100">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-3xl mx-auto mb-12 flex flex-col gap-4">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide text-black">
              Galleri & Øjeblikke
            </h2>
            <p className="text-base text-foreground/70 leading-relaxed">
              Et indblik i undervisningen, koncerterne, fællesskabet og det musikalske univers. Klik på et billede for at se det i stor visning.
            </p>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {previewImages.map((imgUrl, index) => (
              <button
                key={index}
                onClick={() => openLightbox(index)}
                className="group relative overflow-hidden rounded-none shadow-sm aspect-[4/3] border border-sand-200 focus:outline-none"
              >
                <img
                  src={`${imgUrl}?width=400&height=300&fit=cover&format=auto`}
                  alt={`Galleri preview ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <i className="fa-light fa-magnifying-glass-plus text-white text-2xl drop-shadow-md" />
                </div>
              </button>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/galleri"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-widest font-bold text-black hover:text-black/70 transition-colors group"
            >
              Se alle gallerier
              <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* 7. ANMELDELSER */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 text-center">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide text-black">
              Hvad mine sangere siger
            </h2>
          </div>
          <ReviewSlider />
        </div>
      </section>

      {/* 8. KONTAKT CTA */}
      <section className="py-20 md:py-28 text-clay-800 relative overflow-hidden bg-white">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/sean-o-406693_2.jpg?width=1920&quality=60&format=auto"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center flex flex-col gap-6 items-center">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide leading-tight text-black">
            Klar til at finde din personlige stemme?
          </h2>
          <p className="max-w-2xl text-base sm:text-lg text-black leading-relaxed">
            Uanset om du er begynder eller øvet, ung eller ældre, hjælper jeg dig med at styrke din modighedsmuskel og finde glæde og ro i sangen.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mt-4">
            <Link
              to="/kontakt"
              className="rounded-full bg-cta text-clay-900 hover:bg-cta-hover px-8 py-3.5 text-sm uppercase tracking-widest font-semibold transition-all shadow-lg active:scale-98"
            >
              Book en tid nu
            </Link>
            <a
              href="tel:27794688"
              className="rounded-full border border-black text-black hover:-translate-y-1 hover:bg-transparent px-8 py-3.5 text-sm uppercase tracking-widest font-semibold transition-transform duration-300 active:scale-98 inline-flex items-center gap-2"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              27 79 46 88
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImageIndex !== null && (
        <GalleryModal
          images={selectedGalleryImages}
          currentIndex={activeImageIndex}
          onClose={() => setActiveImageIndex(null)}
          onPrev={() => setActiveImageIndex((prev) => (prev === 0 ? selectedGalleryImages.length - 1 : prev - 1))}
          onNext={() => setActiveImageIndex((prev) => (prev === selectedGalleryImages.length - 1 ? 0 : prev + 1))}
        />
      )}
    </div>
  );
}
